/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Maximize2,
  Minimize2,
  Plus,
  Printer,
  RefreshCcw,
  User,
} from "react-feather";
import { FaRegBookmark } from "react-icons/fa";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getAllCharges } from "../../../api/finance/addChargesApi";
import {
  calculateCreditDebitTax,
  downloadCreditDebitDocument,
  downloadCreditDebitPrePrintedDocument,
  getPrePrintState,
  getPrintState,
  updateCreditDebitRemark,
  updatePrePrintState,
  updatePrintState,
} from "../../../api/finance/creditDebitNoteApi";
import {
  getBulkDropdownData,
  getFieldValuesByTableAndId,
} from "../../../api/keyvalue";
import { activeById, FORMS, MODULES, statusById } from "../../../const";
import useUserCompany from "../../../hooks/useUserCompany";
import Grid from "../../Grid";
import Button from "../../input/Button";
import Dropdown from "../../input/Dropdown";
import Input from "../../input/Input";
import LoadingSpinner from "../../LoadingSpinner";
import ButtonRenderer from "../../renderer/ButtonRenderer";
import SelectRenderer from "../../renderer/SelectRenderer";
import {
  CurrencyRenderer,
  DynamicCurrencyRenderer,
  ResizableScrollingTextRenderer,
} from "../../renderer/StyleRenderer";
import ChargeTaxBreakdown from "../EvInv/ChargeTaxBreakdown";
import InfoList from "../../InfoList";
import { formatFromJsonDate } from "../../../dateConfig";

const noteCategory = {
  CREDIT: 1,
  DEBIT: 2,
};

const invoiceCategory = {
  SINGLE: 1,
  BULK: 2,
};

const AccordionContent = ({ isOpen, children }) => {
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    if (contentRef.current) {
      // When open, set maxHeight to the scrollHeight; when closed, set to 0
      setMaxHeight(isOpen ? `${contentRef.current.scrollHeight}px` : "0px");
    }
  }, [isOpen, children]);

  return (
    <div
      style={{
        maxHeight: maxHeight,
        overflowY: "auto",
        opacity: isOpen ? 1 : 0,
        transition: "max-height 0.5s ease, opacity 0.5s ease",
        padding: "0 1rem 0.5rem 1rem",
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

const SummaryDetail = ({ data }) => {
  // Destructure the main fields from the passed data
  const {
    eihType,
    eihCode,
    eihWithoutTax,
    eihWithTax,
    chargeList,
    eihCurrency,
    eihActive,
    eihStatus,
    eihInvCustomer,
    eihInvCustomerName,
    eihBillingCustomer,
    eihTransporterName,
    createdDate,
  } = data;

  const userCompany = useUserCompany();
  const userCurrency = userCompany ? userCompany.comCurrency : null;
  const userCurrencyCode = userCompany ? userCompany.comCurrencyCode : null;
  const [statusName, setStatusName] = useState("");
  const [activeName, setActiveName] = useState("");
  const [currencyName, setCurrencyName] = useState(userCurrencyCode);
  // const [invoiceCustomerName, setInvoiceCustomerName] = useState("");
  const [billingCustomerName, setBillingCustomerName] = useState("");
  const [jobOptions, setJobOptions] = useState([]);
  const [jobId, setJobId] = useState("");
  const [jobObject, setJobObject] = useState({});

  useEffect(() => {
    (async () => {
      const activeName = activeById[eihActive];
      setActiveName(activeName);
      const statusName = statusById[eihStatus];
      setStatusName(statusName);

      const currencyDetails = await getFieldValuesByTableAndId({
        tableName: "masterdata.tms_vm_reg_currency",
        primaryKey: "cur_id",
        fieldNames: "cur_currency",
        ids: eihCurrency.toString(),
      });
      const currName =
        currencyDetails && currencyDetails.length > 0
          ? currencyDetails[0].field1
          : "";
      setCurrencyName(currName);

      // const invCustomer = await getFieldValuesByTableAndId({
      //   tableName: "masterdata.tms_m_stakeholder",
      //   primaryKey: "stk_id",
      //   fieldNames: "stk_name",
      //   ids: eihInvCustomer ? eihInvCustomer.toString() : "",
      // });

      // const invCustomerName =
      //   invCustomer && invCustomer.length > 0 ? invCustomer[0].field1 : "";
      // setInvoiceCustomerName(invCustomerName);

      const billingCustomer = await getFieldValuesByTableAndId({
        tableName: "masterdata.tms_m_stakeholder",
        primaryKey: "stk_id",
        fieldNames: "stk_name",
        ids: eihBillingCustomer ? eihBillingCustomer.toString() : "",
      });

      const billingCustomerName =
        billingCustomer && billingCustomer.length > 0
          ? billingCustomer[0].field1
          : "";
      setBillingCustomerName(billingCustomerName);

      //for summary dropdown
      let jobSet = [];
      for (let jobCharge of chargeList) {
        jobSet.push({
          key: jobCharge.jobId,
          text: `${jobCharge.jobCode} (${jobCharge.chhCode})`,
          value: jobCharge.jobId,
        });
      }
      setJobOptions(jobSet);
      setJobId(jobSet[0].value);
      function mapJobChargesWithJobId(data) {
        return data.reduce((acc, job) => {
          job.jobCharges.forEach((charge) => {
            if (!acc[charge.eidJob]) {
              acc[charge.eidJob] = [];
            }
            acc[charge.eidJob].push({
              jobCode: charge.eidJobCode,
              chargeCode: charge.chhCode,
              chargeType: charge.eidChargeValue,
              currency: charge.eidCurrencyName,
              total: formatValuesWithCurrency(
                formatToTwoDecimalPlaces(charge.eidTotal)
              ),
              status: charge.eidStatusValue,
              active: charge.eidActiveValue,
            });
          });
          return acc;
        }, {});
      }
      const jobChargesSet = mapJobChargesWithJobId(chargeList);
      console.log("jobCharges", jobChargesSet);
      setJobObject(jobChargesSet);
    })();
  }, [
    data,
    eihActive,
    eihStatus,
    eihCurrency,
    eihInvCustomer,
    eihBillingCustomer,
  ]);

  const formatToTwoDecimalPlaces = (value) => {
    if (value == null || value === "N/A") {
      return "N/A";
    }

    const num = parseFloat(value);
    if (isNaN(num)) return "N/A";

    return Number(num.toFixed(2)); // convert back to number!
  };

  const formatValuesWithCurrency = (value) => {
    if (value == null || value === "N/A") {
      return "N/A";
    }

    const formatter = Intl.NumberFormat("en-US", {
      style: "currency",
      currency: userCurrencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(value);
  };

  return (
    <div>
      <h2
        style={{
          paddingTop: "1rem",
        }}
      >
        {eihType === 2 ? "EV" : "INV"} Summary
      </h2>
      <hr />
      <div
        style={{
          marginBottom: "1rem",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>
          <p>
            <strong>Invoice Code : </strong> {eihCode ? eihCode : "N/A"}
          </p>
          <p>
            <strong>Type : </strong> {eihType === 2 ? "EV" : "INV"}
          </p>
          <p>
            <strong>Invoice Customer : </strong>{" "}
            {eihInvCustomerName ? eihInvCustomerName : "N/A"}
          </p>
          <p>
            <strong>Transporter : </strong>{" "}
            {eihTransporterName ? eihTransporterName : "N/A"}
          </p>
          <p>
            <strong>Billing Customer : </strong>{" "}
            {billingCustomerName ? billingCustomerName : "N/A"}
          </p>
          <p>
            <strong>Created On : </strong> {createdDate.split("T")[0]}
          </p>
        </div>
        <div>
          <p>
            <strong>Currency : </strong> {currencyName ? currencyName : "N/A"}
          </p>
          <p>
            <strong>Amount (Without Tax) : </strong>{" "}
            {formatValuesWithCurrency(formatToTwoDecimalPlaces(eihWithoutTax))}
          </p>
          <p>
            <strong>Amount (With Tax) : </strong>{" "}
            {formatValuesWithCurrency(formatToTwoDecimalPlaces(eihWithTax))}
          </p>
          <p>
            <strong>Status : </strong> {statusName ? statusName : "N/A"}
          </p>
          <p>
            <strong>Active : </strong> {activeName ? activeName : "N/A"}
          </p>
        </div>
      </div>
      <div className="ui form segment">
        <div className="two fields">
          <Dropdown
            label="Select job to see Charges"
            clearable={false}
            options={jobOptions}
            value={jobId}
            onChange={(e, { value }) => {
              setJobId(value);
            }}
            placeholder="Select Job"
          />
        </div>
        {chargeList && chargeList.length > 0 ? (
          jobObject[jobId]?.map((job, index) => (
            <div
              key={index}
              style={{
                marginBottom: "0.5rem",
                marginTop: "0.5rem",
              }}
            >
              <ul
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <li style={{ marginBottom: "0.5rem" }}>
                  <span>
                    <strong>Charge Type:</strong> {job.chargeType} |{" "}
                  </span>
                  <span>
                    <strong>Total:</strong> {job.total}
                  </span>
                </li>
              </ul>
            </div>
          ))
        ) : (
          <p>No charge details available.</p>
        )}
      </div>
    </div>
  );
};

const CreateCreditDebitNote = ({
  invoiceDetails,
  editMode,
  noteType,
  creditDebitDetails,
  handleDrawerClose,
  remark,
  setRemark,
  cdActive,
  setUpdateNeeded,
}) => {
  const user = useSelector((state) => state.user);
  const [jobCodesOptions, setJobCodesOptions] = useState([]);
  const [selectRendererJobOptions, setSelectRendererJobOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState(1);
  const [existingCharges, setExistingCharges] = useState({});
  const [chargeTypeOptions, setChargeTypeOptions] = useState([]);
  const [activeAccordion, setActiveAccordion] = useState(false);
  const [chargeList, setChargeList] = useState([]);
  const [rowCount, setRowCount] = useState(1);
  const [currentErrors, setCurrentErrors] = useState({});
  const userCompany = useUserCompany();
  const userCurrencyCode = userCompany ? userCompany.comCurrencyCode : null;
  const [existingTotalSet, setExistingTotalSet] = useState([]);
  const [statusOpt, setStatusOpt] = useState([]);
  const [activeOpt, setActiveOpt] = useState([]);
  const [chargeOpt, setChargeOpt] = useState([]);
  const [uomOpt, setUomOpt] = useState([]);
  const [detailListAgainstChhId, setDetailListAgainstChhId] = useState({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isDataAdding, setIsDataAdding] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrePrintedDownloading, setIsPrePrintedDownloading] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState({
    jobEvInvoicesTaxDetailList: {},
  });
  const [total, setTotal] = useState("");
  const [entireAmount, setEntireAmount] = useState("");
  const [entireTax, setEntireTax] = useState("");

  const auth = useSelector((state) => state.user.permissions[MODULES.Finance]);
  const cdNotePermission = auth[FORMS?.CREDIT_DEBIT.CD_NOTE?.form];
  const printPermission = cdNotePermission?.FORM?.DOWNLOAD;

  const [company, setCompany] = useState(
    userCompany ? userCompany.comId : null
  );

  console.log("Invoice Details", invoiceDetails);

  useEffect(() => {
    setCompany(userCompany ? userCompany.comId : null);
  }, [userCompany]);

  const [gridRow, setGridRow] = useState({
    chargeId: null,
    chargeName: "",
    chargeType: null,
    chargeTypeName: "",
    chargeValue: "",
    existingTotalValue: "",
    taxAmount: "",
    totalValue: "",
    noteType: noteType,
    noteTypeName: noteType === noteCategory.DEBIT ? "Debit" : "Credit",
    status: 2,
    statusName: "",
    active: 1,
    activeName: "",
    qty: "",
    uom: 1,
    uomName: "",
    taxList: [],
    newLine: false,
    existingRecord: false,
  });

  console.log("jobCodeOptions", jobCodesOptions);
  const formatter = useMemo(
    () =>
      userCurrencyCode
        ? Intl.NumberFormat("en-US", {
            style: "currency",
            currency: userCurrencyCode,
          })
        : null,
    [userCurrencyCode]
  );

  console.log("jobCodesOpt", jobCodesOptions);
  console.log("chargeOpt", chargeOpt);

  useEffect(() => {
    console.log("invoiceDetails", invoiceDetails);
    setIsLoading(true);

    let jobSet = [];
    for (let jobCharge of invoiceDetails?.chargeList) {
      jobSet.push({
        key: jobCharge.chhId,
        text: `${jobCharge.jobCode} (${jobCharge.chhCode})`,
        value: jobCharge.chhId,
      });
    }
    setJobCodesOptions(jobSet);

    let selectRendererJobSet = [];
    for (let jobCharge of invoiceDetails?.chargeList) {
      selectRendererJobSet.push({
        id: jobCharge.chhId,
        text: `${jobCharge.jobCode} (${jobCharge.chhCode})`,
        value: jobCharge.chhId,
      });
    }
    setSelectRendererJobOptions(selectRendererJobSet);

    function mapJobCharges(data) {
      return data.reduce((acc, job) => {
        job.jobCharges.forEach((charge) => {
          if (!acc[charge.chhId]) {
            acc[charge.chhId] = [];
          }
          acc[charge.chhId].push({
            key: charge.eidId,
            text: `${charge.eidChargeValue} (${charge.eidTotal})`,
            value: charge.eidId,
          });
        });
        return acc;
      }, {});
    }

    const chargeTypes = mapJobCharges(invoiceDetails?.chargeList);
    setExistingCharges(chargeTypes);

    function mapExistingTotal(data) {
      return data.reduce((acc, job) => {
        job.jobCharges.forEach((charge) => {
          if (!acc[charge.eidId]) {
            acc[charge.eidId] = [];
          }
          acc[charge.eidId].push({
            existingTotal: charge.eidTotal,
            existingUom: charge.eidUom,
            existingUomName: charge.eidUomValue,
            existingQty: charge.eidQty,
            existingStatus: charge.eidStatus,
            existingStatusName: charge.eidStatusValue,
            existingActive: charge.eidActive,
            existingActiveName: charge.eidActiveValue,
          });
        });
        return acc;
      }, {});
    }

    const existingCharges = mapExistingTotal(invoiceDetails?.chargeList);
    setExistingTotalSet(existingCharges);

    function getDetailsUsingChhId(data) {
      let chhWiseCharges = {};
      for (let charge of data) {
        chhWiseCharges = {
          ...chhWiseCharges,
          [charge.chhId]: chhWiseCharges[charge.chhId]
            ? {
                ...chhWiseCharges[charge.chhId],
                ...charge,
                jobCharges: charge.jobCharges.reduce((acc, jc) => {
                  acc[jc.eidId] = jc;
                  return acc;
                }, {}),
              }
            : {
                ...charge,
                jobCharges: charge.jobCharges.reduce((acc, jc) => {
                  acc[jc.eidId] = jc;
                  return acc;
                }, {}),
              },
        };
      }
      return chhWiseCharges;
    }

    const detailsAgainstChhId = getDetailsUsingChhId(
      invoiceDetails?.chargeList
    );
    setDetailListAgainstChhId(detailsAgainstChhId);

    if (editMode) {
      let existingList = [];
      for (let item of creditDebitDetails?.dcdList) {
        if (item.dcdNewLine) {
          existingList.push({
            dcdId: item.dcdId,
            dcdCode: item.dcdCode,
            dcdJob: item.dcdJob,
            dcdDescription: item.dcdDescription,
            dcdCurrency: item.dcdCurrency,
            totalValue: item.dcdTotal, //
            dcdTax: item.dcdTax, //
            taxAmount: item.dcdTaxAmount, //
            dcdExcId: item.dcdExcId,
            dcdExcRate: item.dcdExcRate,
            qty: item.dcdQty,
            uom: item.dcdUom,
            uomName: item.dcdUomName,
            chargeId: item.dcdChhId,
            chargeName: jobSet?.find((j) => j.value === item.dcdChhId).text,
            chargeType: item.dcdCharge,
            chargeTypeId: item.dcdCharge,
            chargeTypeName: item.dcdChargeName,
            chargeValue: item.dcdValue ? parseFloat(item.dcdValue) : 0,
            existingTotalValue: 0,
            noteType: noteType,
            noteTypeName: noteType === noteCategory.DEBIT ? "Debit" : "Credit",
            status: item.dcdStatus,
            statusName: item.dcdStatusName,
            active: item.dcdActive,
            activeName: item.dcdActiveName,
            taxList: item.taxList,
            newLine: true,
            existingRecord: true,
          });
        } else {
          existingList.push({
            dcdId: item.dcdId,
            dcdCode: item.dcdCode,
            dcdJob: item.dcdJob,
            chargeId: item.dcdChhId,
            chargeName: jobSet?.find((j) => j.value === item.dcdChhId).text, //item.dcdChhId,
            chargeType: item.dcdEidId,
            chargeTypeId: item.dcdCharge,
            chargeTypeName: item.dcdChargeName,
            chargeValue: item.dcdValue ? parseFloat(item.dcdValue) : 0,
            dcdDescription: item.dcdDescription,
            dcdCurrency: item.dcdCurrency,
            totalValue: item.dcdTotal, //
            dcdTax: item.dcdTax, //
            taxAmount: item.dcdTaxAmount, //
            dcdExcId: item.dcdExcId,
            dcdExcRate: item.dcdExcRate,
            qty: item.dcdQty,
            uom: item.dcdUom,
            uomName: item.dcdUomName,
            existingTotalValue: item.dcdExistingValue
              ? parseFloat(item.dcdExistingValue)
              : 0,
            noteType: noteType,
            noteTypeName: noteType === noteCategory.DEBIT ? "Debit" : "Credit",
            status: item.dcdStatus,
            statusName: item.dcdStatusName,
            active: item.dcdActive,
            activeName: item.dcdActiveName,
            taxList: item.taxList,
            newLine: false,
            existingRecord: true,
          });
        }
      }
      let rows = existingList?.length + 1;
      setRowCount(rows);
      setChargeList(existingList);

      const total = existingList.reduce(
        (acc, curr) => acc + formatToTwoDecimalPlaces(curr.totalValue),
        0
      );
      setTotal(formatValuesWithCurrency(total));

      const taxTotal = existingList.reduce(
        (acc, curr) => acc + formatToTwoDecimalPlaces(curr.taxAmount),
        0
      );
      setEntireTax(formatValuesWithCurrency(taxTotal));

      const amount = existingList.reduce(
        (acc, curr) => acc + formatToTwoDecimalPlaces(curr.chargeValue),
        0
      );
      setEntireAmount(formatValuesWithCurrency(amount));
    }

    setRemark(creditDebitDetails?.dcrRemarks);
    setIsLoading(false);
  }, [invoiceDetails, editMode]);

  console.log("details", creditDebitDetails);

  useEffect(() => {
    if (gridRow.chargeId) {
      const chOpt = (chhId) => existingCharges[chhId] || [];
      setChargeTypeOptions(chOpt(gridRow.chargeId));
    }
  }, [gridRow.chargeId, existingCharges]);

  const resetForm = () => {
    setGridRow({
      chargeId: null,
      chargeName: "",
      chargeType: null,
      chargeTypeName: "",
      chargeValue: "",
      taxAmount: "",
      totalValue: "",
      noteType: noteType,
      noteTypeName: noteType === noteCategory.DEBIT ? "Debit" : "Credit",
      status: 2,
      statusLabel: "",
      active: 1,
      activeLabel: "",
      qty: "",
      uom: 1,
      uomName: "",
      existingTotalValue: "",
      taxList: [],
      newLine: false,
      existingRecord: false,
    });

    setCurrentErrors({});
    setChargeTypeOptions([]);
  };

  const resetGrid = () => {
    resetForm();
    setChargeList([]);
    setRemark("");
  };

  const validateAddClick = () => {
    let errors = {};
    if (!gridRow.chargeId || gridRow.chargeId === null) {
      errors = {
        ...errors,
        chargeId: "Job is required",
      };
    }

    if (!gridRow.chargeType || gridRow.chargeType === null) {
      errors = {
        ...errors,
        chargeType: "Charge Type is required",
      };
    }

    if (gridRow.chargeValue === "") {
      errors = {
        ...errors,
        chargeValue: "Value is required",
      };
    }
    return errors;
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const {
        status,
        active,
        uom: uom,
      } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
        { key: "uom" },
      ]);
      setStatusOpt(status);
      setActiveOpt(active);
      setUomOpt(uom);

      const charges = await getAllCharges();
      setChargeOpt(charges);

      setIsLoading(false);
    })();
  }, [editMode]);

  const formatToTwoDecimalPlaces = (value) => {
    if (value == null || value === "N/A") {
      return "N/A";
    }

    const num = parseFloat(value);
    if (isNaN(num)) return "N/A";

    return Number(num.toFixed(2)); // convert back to number!
  };

  const formatValuesWithCurrency = (value) => {
    if (value == null || value === "N/A") {
      return "N/A";
    }
    const formatter = Intl.NumberFormat("en-US", {
      style: "currency",
      currency: userCurrencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(value);
  };

  const handleAdd = async () => {
    setIsDataAdding(true);
    const errors = validateAddClick();
    if (Object.keys(errors).length > 0) {
      setCurrentErrors(errors);
      toast.error("Please Fill the required fields", {
        autoClose: 5000,
        containerId: "Main",
      });
      setIsDataAdding(false);
      return;
    }

    const activeValue = activeById[gridRow.active];
    const statusValue = statusById[gridRow.status];

    const charge = jobCodesOptions?.find(
      (item) => item.value === gridRow.chargeId
    );
    const chgName = charge ? charge.text : "";

    const chgType = chargeTypeOptions?.find(
      (item) => item.value === gridRow.chargeType
    );
    const chgTypeName = chgType ? chgType.text : "";

    const taxes = await calculateCreditDebitTax({
      chId: detailListAgainstChhId[gridRow.chargeId].jobCharges[
        gridRow.chargeType
      ].eidCharge,
      chAmount: gridRow.chargeValue ? parseFloat(gridRow.chargeValue) : 0,
      stakeholderId:
        invoiceDetails?.eihType === 2
          ? invoiceDetails?.eihTransporter
          : invoiceDetails?.eihInvCustomer,
    });

    let totTax = 0;
    if (taxes.length > 0) {
      totTax = taxes?.map((item) => formatToTwoDecimalPlaces(item.taxAmount)).reduce((a, b) => a + b, 0);
    }

    let total = 0;
    if (noteType === noteCategory.DEBIT) {
      total =
        formatToTwoDecimalPlaces(parseFloat(gridRow.existingTotalValue)) +
        formatToTwoDecimalPlaces(parseFloat(totTax)) +
        formatToTwoDecimalPlaces(parseFloat(gridRow.chargeValue));
    } else {
      total =
        formatToTwoDecimalPlaces(parseFloat(gridRow.existingTotalValue)) -
        formatToTwoDecimalPlaces(parseFloat(totTax)) -
        formatToTwoDecimalPlaces(parseFloat(gridRow.chargeValue));
    }

    if (!gridRow.dcdId || gridRow.dcdId === null) {
      if (!gridRow.id || gridRow.id === null) {
        setChargeList((prev) => [
          ...prev,
          {
            ...gridRow,
            id: rowCount,
            chargeName: chgName,
            chargeTypeName: chgTypeName,
            taxAmount: totTax,
            totalValue: total,
            activeName: activeValue,
            statusName: statusValue,
            taxList: taxes?.map((ti) => ({
              eitReference: null,
              eitCompany: company,
              eitTad: ti.tadId,
              eitTax: ti.taxId,
              taxName: ti.taxTax,
              eitTadValue: ti.taxPercentage,
              eitValue: ti.taxAmount,
              eitStatus: 2,
              eitActive: 1,
              beforeTax: ti.beforeTaxAmount,
              afterTax: ti.afterTaxAmount,
              author: user.username,
            })),
          },
        ]);

        setRowCount((prevState) => prevState + 1);
      } else {
        //update
        setChargeList((prevState) =>
          prevState.filter((item) => item.id !== gridRow.id)
        );
        setChargeList((prev) => [
          ...prev,
          {
            ...gridRow,
            chargeName: chgName,
            chargeTypeName: chgTypeName,
            taxAmount: totTax,
            totalValue: total,
            activeName: activeValue,
            statusName: statusValue,
            taxList: taxes?.map((ti) => ({
              eitReference: null,
              eitCompany: company,
              eitTad: ti.tadId,
              eitTax: ti.taxId,
              taxName: ti.taxTax,
              eitTadValue: ti.taxPercentage,
              eitValue: ti.taxAmount,
              eitStatus: 2,
              eitActive: 1,
              beforeTax: ti.beforeTaxAmount,
              afterTax: ti.afterTaxAmount,
              author: user.username,
            })),
          },
        ]);
      }
    } else {
      setChargeList((prevState) =>
        prevState.filter((item) => item.dcdId !== gridRow.dcdId)
      );

      setChargeList((prev) => [
        ...prev,
        {
          ...gridRow,
          chargeName: chgName,
          chargeTypeName: chgTypeName,
          taxAmount: totTax,
          totalValue: total,
          activeName: activeValue,
          statusName: statusValue,
          taxList: taxes?.map((ti) => ({
            eitReference: null,
            eitCompany: company,
            eitTad: ti.tadId,
            eitTax: ti.taxId,
            taxName: ti.taxTax,
            eitTadValue: ti.taxPercentage,
            eitValue: ti.taxAmount,
            eitStatus: 2,
            eitActive: 1,
            beforeTax: ti.beforeTaxAmount,
            afterTax: ti.afterTaxAmount,
            author: user.username,
          })),
        },
      ]);
    }

    resetForm();

    setIsDataAdding(false);
  };

  const addNewLine = () => {
    const newRow = {
      id: rowCount,
      chargeId: null,
      chargeName: "",
      chargeType: null,
      chargeTypeName: "",
      chargeValue: 0,
      taxAmount: "",
      totalValue: "",
      existingTotalValue: 0,
      noteType: noteType,
      noteTypeName: noteType === noteCategory.DEBIT ? "Debit" : "Credit",
      status: 2,
      statusName: "",
      active: 1,
      activeName: "",
      qty: 1,
      uom: 1,
      uomName: "",
      taxList: [],
      newLine: true,
      existingRecord: false,
    };

    setChargeList([...chargeList, newRow]);
    setRowCount((prevState) => prevState + 1);
  };

  const cellEditorParams = async (params) => {
    switch (params.colDef.field) {
      case "chargeValue": {
        setIsDataAdding(true);
        let value = Number(params.value);
        let amount = parseFloat(value) * parseFloat(params.data.qty);

        const taxes = await calculateCreditDebitTax({
          chId: params.data.chargeType,
          chAmount: amount ? amount : 0,
          stakeholderId:
            invoiceDetails?.eihType === 2
              ? invoiceDetails?.eihTransporter
              : invoiceDetails?.eihInvCustomer,
        });

        let totTax = 0;
        if (taxes.length > 0) {
          totTax = taxes
            .map((item) => formatToTwoDecimalPlaces(item.taxAmount))
            .reduce((a, b) => a + b, 0);
        }

        let total1 = 0;
        if (noteType === noteCategory.DEBIT) {
          total1 =
            formatToTwoDecimalPlaces(parseFloat(params.data.existingTotalValue)) +
            formatToTwoDecimalPlaces(parseFloat(totTax)) +
            formatToTwoDecimalPlaces(parseFloat(amount));
        } else {
          total1 =
            formatToTwoDecimalPlaces(parseFloat(params.data.existingTotalValue)) -
            formatToTwoDecimalPlaces(parseFloat(totTax)) -
            formatToTwoDecimalPlaces(parseFloat(amount));
        }

        setChargeList((prev) =>
          prev.map((row) => {
            if (!params.data.dcdId) {
              if (row.id === params.data.id) {
                return {
                  ...row,
                  chargeValue: parseFloat(value),
                  taxAmount: totTax,
                  totalValue: total1,
                  taxList: taxes?.map((ti) => ({
                    eitReference: null,
                    eitCompany: company,
                    eitTad: ti.tadId,
                    eitTax: ti.taxId,
                    taxName: ti.taxTax,
                    eitTadValue: ti.taxPercentage,
                    eitValue: ti.taxAmount,
                    eitStatus: 2,
                    eitActive: 1,
                    beforeTax: ti.beforeTaxAmount,
                    afterTax: ti.afterTaxAmount,
                    author: user.username,
                  })),
                };
              }
            } else {
              if (row.dcdId === params.data.dcdId) {
                return {
                  ...row,
                  chargeValue: parseFloat(value),
                  taxAmount: totTax,
                  totalValue: total1,
                  taxList: taxes?.map((ti) => ({
                    eitReference: null,
                    eitCompany: company,
                    eitTad: ti.tadId,
                    eitTax: ti.taxId,
                    taxName: ti.taxTax,
                    eitTadValue: ti.taxPercentage,
                    eitValue: ti.taxAmount,
                    eitStatus: 2,
                    eitActive: 1,
                    beforeTax: ti.beforeTaxAmount,
                    afterTax: ti.afterTaxAmount,
                    author: user.username,
                  })),
                };
              }
            }
            return row;
          })
        );
        setIsDataAdding(false);
        break;
      }
      case "qty": {
        setIsDataAdding(true);
        let value = Number(params.value);
        let amount = parseFloat(value) * parseFloat(params.data.chargeValue);

        const taxes = await calculateCreditDebitTax({
          chId: params.data.chargeType,
          chAmount: amount ? amount : 0,
          stakeholderId:
            invoiceDetails?.eihType === 2
              ? invoiceDetails?.eihTransporter
              : invoiceDetails?.eihInvCustomer,
        });

        let totTax = 0;
        if (taxes.length > 0) {
          totTax = taxes
            .map((item) => formatToTwoDecimalPlaces(item.taxAmount))
            .reduce((a, b) => a + b, 0);
        }

        let total = 0;
        if (noteType === noteCategory.DEBIT) {
          total =
            formatToTwoDecimalPlaces(parseFloat(params.data.existingTotalValue)) +
            formatToTwoDecimalPlaces(parseFloat(totTax)) +
            formatToTwoDecimalPlaces(parseFloat(amount));
        } else {
          total =
            formatToTwoDecimalPlaces(parseFloat(params.data.existingTotalValue)) -
            formatToTwoDecimalPlaces(parseFloat(totTax)) -
            formatToTwoDecimalPlaces(parseFloat(amount));
        }

        setChargeList((prev) =>
          prev.map((row) => {
            if (!params.data.dcdId) {
              if (row.id === params.data.id) {
                return {
                  ...row,
                  qty: value,
                  taxAmount: totTax,
                  totalValue: total,
                  taxList: taxes?.map((ti) => ({
                    eitReference: null,
                    eitCompany: company,
                    eitTad: ti.tadId,
                    eitTax: ti.taxId,
                    taxName: ti.taxTax,
                    eitTadValue: ti.taxPercentage,
                    eitValue: ti.taxAmount,
                    eitStatus: 2,
                    eitActive: 1,
                    beforeTax: ti.beforeTaxAmount,
                    afterTax: ti.afterTaxAmount,
                    author: user.username,
                  })),
                };
              }
            } else {
              if (row.dcdId === params.data.dcdId) {
                return {
                  ...row,
                  qty: value,
                  taxAmount: totTax,
                  totalValue: total,
                  taxList: taxes?.map((ti) => ({
                    eitReference: null,
                    eitCompany: company,
                    eitTad: ti.tadId,
                    eitTax: ti.taxId,
                    taxName: ti.taxTax,
                    eitTadValue: ti.taxPercentage,
                    eitValue: ti.taxAmount,
                    eitStatus: 2,
                    eitActive: 1,
                    beforeTax: ti.beforeTaxAmount,
                    afterTax: ti.afterTaxAmount,
                    author: user.username,
                  })),
                };
              }
            }
            return row;
          })
        );
        setIsDataAdding(false);
        break;
      }
      default:
        break;
    }
  };

  const handleSave = async () => {
    setIsSubmitLoading(true);
    try {
      const data = {
        dcrId: creditDebitDetails.dcrId,
        dcrRemarks: remark,
        author: user.username,
      };
      const response = await updateCreditDebitRemark(data);

      if (response.status === 200) {
        toast.success(`${response.data.dcrCode} Updated successfully`, {
          autoClose: 5000,
          containerId: "Main",
        });
        resetGrid();
        handleDrawerClose();
        setUpdateNeeded(true);
      } else {
        toast.error(response.data.message, {
          autoClose: 5000,
          containerId: "Main",
        });
      }
      // }
    } catch (e) {
      console.log(e);
      toast.error("Something went wrong, please try again.");
    }
    setIsSubmitLoading(false);
  };

  function numberToEnglishUpper(num) {
    if (typeof num !== "number" || isNaN(num) || num < 0) {
      throw new Error("Input must be a non-negative number");
    }

    // Round to two decimal places
    num = Number(num?.toFixed(2));

    const below20 = [
      "Zero",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const scales = ["", "Thousand", "Million", "Billion", "Trillion"];

    // helper for 0 <= n < 1000
    function intHelper(n) {
      if (n < 20) return below20[n];
      if (n < 100) {
        const t = Math.floor(n / 10);
        const r = n % 10;
        return tens[t] + (r ? " " + below20[r] : "");
      }
      const h = Math.floor(n / 100);
      const rem = n % 100;
      return below20[h] + " Hundred" + (rem ? " " + intHelper(rem) : "");
    }

    // Split into integer+fraction parts
    const [intPartStr, fracPartStr] = num.toString().split(".");

    // Build integer‐part words
    let intNum = parseInt(intPartStr, 10);
    let intWords = "";
    if (intNum === 0) {
      intWords = "Zero";
    } else {
      let scaleIdx = 0;
      while (intNum > 0) {
        const chunk = intNum % 1000;
        if (chunk) {
          const chunkWords = intHelper(chunk);
          intWords =
            chunkWords +
            (scales[scaleIdx] ? " " + scales[scaleIdx] : "") +
            (intWords ? " " + intWords : "");
        }
        intNum = Math.floor(intNum / 1000);
        scaleIdx++;
      }
    }

    // If no fractional part, just return the integer words
    if (!fracPartStr) {
      return intWords.toUpperCase();
    }

    // Otherwise spell out each digit after the decimal point
    const fracWords = Array.from(fracPartStr)
      .map((dig) => below20[Number(dig)])
      .join(" ");

    return (intWords + " Point " + fracWords).toUpperCase();
  }

 const groupChargesByType = (charges) => {
  const grouped = charges?.reduce((acc, charge) => {
    const key = charge.chargeTypeId; 
    if (!acc[key]) {
      acc[key] = {
        chargeTypeId: key,
        chargeTypeName: charge.chargeTypeName,
        chargeValue: 0,
      };
    }
    acc[key].chargeValue += parseFloat(charge.chargeValue) || 0;
    return acc;
  }, {});
  return grouped ? Object.values(grouped) : [];
};

  useEffect(() => {
    if (chargeList?.length > 0) {
      const total = chargeList?.reduce(
        (acc, curr) => acc + formatToTwoDecimalPlaces(curr.totalValue),
        0
      );
      setTotal(formatValuesWithCurrency(total));

      const taxTotal = chargeList?.reduce(
        (acc, curr) => acc + formatToTwoDecimalPlaces(curr.taxAmount),
        0
      );
      setEntireTax(formatValuesWithCurrency(taxTotal));

      const amount = chargeList?.reduce(
        (acc, curr) => acc + formatToTwoDecimalPlaces(curr.chargeValue),
        0
      );
      setEntireAmount(formatValuesWithCurrency(amount));
    }
  }, [chargeList]);

  const printOptionProceed = async (data) => {
    setIsDownloading(true);

    if (!data || Object.keys(data).length === 0) {
      console.error("No data provided for printing.", {
        autoClose: 5000,
        containerId: "Main",
      });
      setIsDownloading(false);
      return;
    }

    try {
      const res = await getPrintState(data.dcrId);
      const printState = res.data;

      console.log("printState", printState);
      if (res.status !== 200) {
        toast.error("Something went wrong, please try again.", {
          autoClose: 5000,
          containerId: "Main",
        });
        setIsDownloading(false);
        return;
      }

      let topic = "";
      if (!printState) {
        topic = `${data.creditDebitTypeValue && data.creditDebitTypeValue !== null ? data.creditDebitTypeValue : "INVOICE"} ${noteType === noteCategory.DEBIT ? "DEBIT" : "CREDIT"} NOTE | ORIGINAL`;
      } else {
        topic = `${data.creditDebitTypeValue && data.creditDebitTypeValue !== null ? data.creditDebitTypeValue : "INVOICE"} ${noteType === noteCategory.DEBIT ? "DEBIT" : "CREDIT"} NOTE | COPY`;
      }

      const totWithoutTax = chargeList?.reduce(
        (total, item) => total + item.chargeValue,
        0
      );
      const totTax = chargeList?.reduce(
        (total, item) => total + item.taxAmount,
        0
      );

      const totWithTax = totWithoutTax + totTax;
      let gridData;
       
      const dcrType = data.dcrType || 2;
      const dcrSingleBulk = data.dcrSingleBulk || 1;

      const sourceCharges =
      dcrSingleBulk === invoiceCategory.BULK
       ? groupChargesByType(chargeList)
       : chargeList;

      if (dcrType === 1) {
        gridData = sourceCharges?.map((charge) => ({
        chargeTypeName: charge.chargeTypeName,
        qty: 1,
        chargeValue: formatValuesWithCurrency(
        formatToTwoDecimalPlaces(charge.chargeValue)
           ),
         totalCharge: formatValuesWithCurrency(
        formatToTwoDecimalPlaces(1 * charge.chargeValue)
          ),
        }));
      } else {
          gridData = sourceCharges?.map((charge) => ({
          chargeTypeName: charge.chargeTypeName,
          chargeValue: formatValuesWithCurrency(
          formatToTwoDecimalPlaces(charge.chargeValue)
          ),
        }));
      }        

      const payLoad = {
        code: data.dcrCode,
        customer: data.dcrCustomerValue ? data.dcrCustomerValue : "N/A",
        transporter: data.dcrTransporterValue
          ? data.dcrTransporterValue
          : "N/A",
        billingCustomer: data.dcrBillingCustomerValue
          ? data.dcrBillingCustomerValue
          : "N/A",
        invCustomer: data.dcrInvCustomerValue
          ? data.dcrInvCustomerValue
          : "N/A",
        dcrAddress: data.dcrAddress ? data.dcrAddress : "N/A",
        address: data.address ? data.address : "N/A",
        address2: data.address2 === "N/A" ? "" : data.address2,
        address3: data.address3 === "N/A" ? "" : data.address3,
        currencyId: data.dcrCurrency ? data.dcrCurrency : "N/A",
        curCode: data.curCode ? data.curCode : "N/A",
        curCurrency: data.curCurrency ? data.curCurrency : "N/A",
        remarks: data.dcrRemarks ? data.dcrRemarks : "",
        withoutTax: data.dcrWithoutTax
          ? formatValuesWithCurrency(
              formatToTwoDecimalPlaces(data.dcrWithoutTax)
            )
          : "N/A",
        withTax: data.dcrWithTax
          ? formatValuesWithCurrency(formatToTwoDecimalPlaces(data.dcrWithTax))
          : "N/A",
        createdBy: data.createdBy ? data.createdBy : "",
        createdDate: data.createdDate 
        ? (() => { const [y, m, d] = data.createdDate.split("T")[0].split("-"); return `${m}/${d}/${y}`; })()
        : "",
        cusVatNo: data.vatNo ? data.vatNo : "N/A",
        cusSvatNo: data.svatNo ? data.svatNo : "N/A",
        ourVatNo: data.ourVat ? data.ourVat : "N/A",
        ourSvatNo: data.ourSvat ? data.ourSvat : "N/A",
        totalWithoutTaxTitle: "SUB TOTAL",
        totalWithoutTax: formatValuesWithCurrency(
          formatToTwoDecimalPlaces(totWithoutTax)
        ),
        totalTaxTitle: "TAX",
        totalTax: formatValuesWithCurrency(formatToTwoDecimalPlaces(totTax)),
        totalWithTax: formatValuesWithCurrency(
          formatToTwoDecimalPlaces(totWithTax)
        ),
        noteCreatedBy: user.username,
        noteCreatedDate: new Date().toISOString().slice(0, 10),
        job: data.dcrSingleBulk === 1 ? data.jobCode ? data.jobCode : "N/A" : "",
        evInvNo: invoiceDetails ? invoiceDetails.eihCode : "N/A",
        request: data.dcrSingleBulk === 1 ? data.reqCode ? data.reqCode : "N/A" : "",
        title: topic,
        totalInEnglish: data.curCode + ". " + numberToEnglishUpper(totWithTax),
        gridData: gridData,
        dcrType: dcrType,
        dcrSingleBulk: dcrSingleBulk,
        supCompany:data.supCompany,
        supAdd1:data.supAdd1 ? data.supAdd1 : "" ,
        supAdd2:data.supAdd2 ? data.supAdd2 : "",
        supAdd3:data.supAdd3 ? data.supAdd3 : "",
        supContact: data.supContact ? data.supContact : "",
        place: "Colombo".toUpperCase(),
        purchasertelephoneno:data.purchaserCon? data.purchaserCon : "",
        contNos: data.contNos? data.contNos : "",
        cluster:data.cluster? data.cluster : "",
        consignee:data.consignee? data.consignee : "",
        startLocation:data.startLocation? data.startLocation : "",
        vehicleType:data.vehicleType? data.vehicleType : "",
        jobStart: data.jobStart? data.jobStart.replace("T", " ").split(".")[0] : "",
        jobEnd: data.jobEnd? data.jobEnd.replace("T", " ").split(".")[0] : "",
        totalHrs:data.totalHrs? data.totalHrs : 0,
        endLocation: data.endLocation? data.endLocation : "",
        bankDetails:data.bankDetails? data.bankDetails : "",
      };

      const blob = await downloadCreditDebitDocument(payLoad);
       console.log("print data", payLoad);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${data.dcrCode}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      await updatePrintState(data.dcrId); // update print state
      setIsDownloading(false);
    } catch (e) {
      console.log(e);
      toast.error("Something went wrong, please try again.", {
        autoClose: 5000,
        containerId: "Main",
      });
      setIsDownloading(false);
    } finally {
      setIsDownloading(false);
    }
  };

  const prePrintOptionProceed = async (data) => {
    setIsPrePrintedDownloading(true);

    if (!data || Object.keys(data).length === 0) {
      console.error("No data provided for printing.", {
        autoClose: 5000,
        containerId: "Main",
      });
      setIsPrePrintedDownloading(false);
      return;
    }

    try {
      const res = await getPrePrintState(data.dcrId);
      const printState = res.data;

      console.log("printState", printState);
      if (res.status !== 200) {
        toast.error("Something went wrong, please try again.", {
          autoClose: 5000,
          containerId: "Main",
        });
        setIsPrePrintedDownloading(false);
        return;
      }

      let topic = "";
      if (!printState) {
        topic = `${data.creditDebitTypeValue && data.creditDebitTypeValue !== null ? data.creditDebitTypeValue : "INVOICE"} ${noteType === noteCategory.DEBIT ? "DEBIT" : "CREDIT"} NOTE | ORIGINAL`;
      } else {
        topic = `${data.creditDebitTypeValue && data.creditDebitTypeValue !== null ? data.creditDebitTypeValue : "INVOICE"} ${noteType === noteCategory.DEBIT ? "DEBIT" : "CREDIT"} NOTE | COPY`;
      }

      const totWithoutTax = chargeList?.reduce(
        (total, item) => total + item.chargeValue,
        0
      );
      const totTax = chargeList?.reduce(
        (total, item) => total + item.taxAmount,
        0
      );

      const totWithTax = totWithoutTax + totTax;

      let gridData;
      const dcrType = data.dcrType || 2;
      const dcrSingleBulk = data.dcrSingleBulk || 1;

     const sourceCharges = 
     dcrSingleBulk === invoiceCategory.BULK
    ? groupChargesByType(chargeList)
    : chargeList;

    if (dcrType === 1) {
      gridData = sourceCharges?.map((charge) => ({
      chargeTypeName: charge.chargeTypeName,
      qty: 1, 
      chargeValue: formatValuesWithCurrency(
      formatToTwoDecimalPlaces(charge.chargeValue)
        ),
      totalCharge: formatValuesWithCurrency(
      formatToTwoDecimalPlaces(1 * charge.chargeValue)
          ),
       }));
      } else {
       gridData = sourceCharges?.map((charge) => ({
       chargeTypeName: charge.chargeTypeName,
       chargeValue: formatValuesWithCurrency(
      formatToTwoDecimalPlaces(charge.chargeValue)
         ),
      }));
    }

      const payLoad = {
        code: data.dcrCode,
        customer: data.dcrCustomerValue ? data.dcrCustomerValue : "N/A",
        transporter: data.dcrTransporterValue
          ? data.dcrTransporterValue
          : "N/A",
        billingCustomer: data.dcrBillingCustomerValue
          ? data.dcrBillingCustomerValue
          : "N/A",
        invCustomer: data.dcrInvCustomerValue
          ? data.dcrInvCustomerValue
          : "N/A",
        dcrAddress: data.dcrAddress ? data.dcrAddress : "N/A",
        address: data.address ? data.address : "N/A",
        address2: data.address2 === "N/A" ? "" : data.address2,
        address3: data.address3 === "N/A" ? "" : data.address3,
        currencyId: data.dcrCurrency ? data.dcrCurrency : "N/A",
        curCode: data.curCode ? data.curCode : "N/A",
        curCurrency: data.curCurrency ? data.curCurrency : "N/A",
        remarks: data.dcrRemarks ? data.dcrRemarks : "",
        withoutTax: data.dcrWithoutTax
          ? formatValuesWithCurrency(
              formatToTwoDecimalPlaces(data.dcrWithoutTax)
            )
          : "N/A",
        withTax: data.dcrWithTax
          ? formatValuesWithCurrency(formatToTwoDecimalPlaces(data.dcrWithTax))
          : "N/A",
        createdBy: data.createdBy ? data.createdBy : "",
        createdDate: data.createdDate 
        ? (() => { const [y, m, d] = data.createdDate.split("T")[0].split("-"); return `${m}/${d}/${y}`; })()
        : "",
        cusVatNo: data.vatNo ? data.vatNo : "N/A",
        cusSvatNo: data.svatNo ? data.svatNo : "N/A",
        ourVatNo: data.ourVat ? data.ourVat : "N/A",
        ourSvatNo: data.ourSvat ? data.ourSvat : "N/A",
        totalWithoutTaxTitle: "SUB TOTAL",
        totalWithoutTax: formatValuesWithCurrency(
          formatToTwoDecimalPlaces(totWithoutTax)
        ),
        totalTaxTitle: "TAX",
        totalTax: formatValuesWithCurrency(formatToTwoDecimalPlaces(totTax)),
        totalWithTax: formatValuesWithCurrency(
          formatToTwoDecimalPlaces(totWithTax)
        ),
        noteCreatedBy: user.username,
        noteCreatedDate: new Date().toISOString().slice(0, 10),
        job: data.dcrSingleBulk === 1 ? data.jobCode ? data.jobCode : "N/A" : "",
        evInvNo: invoiceDetails ? invoiceDetails.eihCode : "N/A",
        request: data.dcrSingleBulk === 1 ? data.reqCode ? data.reqCode : "N/A" : "",
        title: topic,
        totalInEnglish: data.curCode + ". " + numberToEnglishUpper(totWithTax),
        gridData: gridData,
        dcrType: dcrType,
        dcrSingleBulk: dcrSingleBulk,
        supCompany:data.supCompany,
        supAdd1:data.supAdd1 ? data.supAdd1 : "" ,
        supAdd2:data.supAdd2 ? data.supAdd2 : "",
        supAdd3:data.supAdd3 ? data.supAdd3 : "",
        supContact: data.supContact ? data.supContact : "",
        place: "Colombo".toUpperCase(),
        purchasertelephoneno:data.purchaserCon? data.purchaserCon : "",
        contNos: data.contNos? data.contNos : "",
        cluster:data.cluster? data.cluster : "",
        consignee:data.consignee? data.consignee : "",
        startLocation:data.startLocation? data.startLocation : "",
        vehicleType:data.vehicleType? data.vehicleType : "",
        jobStart: data.jobStart? data.jobStart.replace("T", " ").split(".")[0] : "",
        jobEnd: data.jobEnd? data.jobEnd.replace("T", " ").split(".")[0] : "",
        totalHrs:data.totalHrs? data.totalHrs : 0,
        endLocation: data.endLocation? data.endLocation : "",
        bankDetails:data.bankDetails? data.bankDetails : "",
        
      };

      const blob = await downloadCreditDebitPrePrintedDocument(payLoad);
      console.log("print data", payLoad);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${data.dcrCode}_pre_print.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      await updatePrePrintState(data.dcrId); // update print state
      setIsPrePrintedDownloading(false);
    } catch (e) {
      console.log(e);
      toast.error("Something went wrong, please try again.", {
        autoClose: 5000,
        containerId: "Main",
      });
      setIsPrePrintedDownloading(false);
    } finally {
      setIsPrePrintedDownloading(false);
    }
  };

  console.log("charge list", chargeList);
  console.log("total", total);

  return (
    <>
      {isSubmitLoading && (
        <div className="blur-overlay">
          <LoadingSpinner
            message={`${editMode ? "Updating" : "Creating"}...`}
          />
        </div>
      )}
      {(isDownloading || isPrePrintedDownloading) && (
        <div className="blur-overlay">
          <LoadingSpinner message={`Downloading...`} />
        </div>
      )}
      {isLoading ? (
        <LoadingSpinner message={`Fetching Details...`} />
      ) : (
        <div className="ui form segment">
          <div className="two fields">
            <Input
              type={"text"}
              label="Remark"
              value={remark}
              placeholder="Type Remark here if required..."
              onChange={(e) => setRemark(e.target.value)}
              readOnly={!cdActive}
            />
          </div>
          <div className="ui styled accordion" style={{ width: "100%" }}>
            <div
              className={`title ${activeAccordion ? "active" : ""}`}
              onClick={() => setActiveAccordion(!activeAccordion)}
              style={{
                cursor: "pointer",
              }}
            >
              {activeAccordion ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    {" "}
                    Click here to hide {invoiceDetails?.eihCode} details
                  </div>
                  <Minimize2 size={14} />
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    Click here to view {invoiceDetails?.eihCode} details
                  </div>
                  <Maximize2 size={14} />
                </div>
              )}
            </div>
            <AccordionContent isOpen={activeAccordion}>
              <SummaryDetail data={invoiceDetails} />
            </AccordionContent>
          </div>

          {chargeList?.length > 0 && (
            <ChargeTaxBreakdown
              formatter={formatter}
              selectedCharge={selectedCharge ? selectedCharge : null}
            />
          )}

          {!editMode && (
            <div className="four fields" style={{ marginTop: "10px" }}>
              <Dropdown
                required={true}
                search={true}
                label="Jobs"
                clearable={false}
                options={jobCodesOptions}
                value={gridRow.chargeId}
                onChange={(e, { value }) => {
                  setGridRow({
                    ...gridRow,
                    chargeId: value,
                  });
                }}
                placeholder="Select Job"
                error={currentErrors.chargeId}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { chargeId: _, ...rest } = prevState;
                    return rest;
                  })
                }
                readOnly={editMode ? true : false}
              />
              <Dropdown
                required={true}
                label="Charge Type"
                clearable={false}
                options={chargeTypeOptions}
                value={gridRow.chargeType}
                onChange={(e, { value }) => {
                  setGridRow({
                    ...gridRow,
                    chargeType: value,
                    existingTotalValue:
                      existingTotalSet[value][0]?.existingTotal,
                    qty: existingTotalSet[value][0]?.existingQty,
                    uom: existingTotalSet[value][0]?.existingUom,
                    uomName: existingTotalSet[value][0]?.existingUomName,
                    status: existingTotalSet[value][0]?.existingStatus,
                    statusName: existingTotalSet[value][0]?.existingStatusName,
                    active: existingTotalSet[value][0]?.existingActive,
                    activeName: existingTotalSet[value][0]?.existingActiveName,
                  });
                }}
                placeholder={
                  !gridRow.chargeId ? "Select Job" : "Select Charge Type"
                }
                error={currentErrors.chargeType}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { chargeType: _, ...rest } = prevState;
                    return rest;
                  })
                }
                readOnly={editMode ? true : false}
              />
              <Input
                type={"text"}
                label="Existing Value"
                value={
                  gridRow.existingTotalValue
                    ? formatValuesWithCurrency(
                        formatToTwoDecimalPlaces(gridRow.existingTotalValue)
                      )
                    : ""
                }
                readOnly={true}
              />
              <Input
                required={true}
                type={"number"}
                label={"Value"}
                step={"0.0001"}
                value={gridRow.chargeValue}
                onChange={(e) =>
                  setGridRow({
                    ...gridRow,
                    chargeValue: parseFloat(e.target.value),
                  })
                }
                error={currentErrors.chargeValue}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { chargeValue: _, ...rest } = prevState;
                    return rest;
                  })
                }
                readOnly={editMode ? true : false}
              />
            </div>
          )}
          {!editMode && (
            <div className="four fields">
              <Input
                type={"number"}
                label={"Existing Qty"}
                step={"0.0001"}
                value={gridRow.qty}
                readOnly={true}
              />
              <Input
                type={"text"}
                placeholder="Existing Uom"
                label="Existing Uom"
                value={gridRow.uomName}
                readOnly={true}
              />
              <Input
                type={"text"}
                placeholder="Existing Status"
                label="Existing Status"
                value={gridRow.statusName}
                readOnly={true}
              />
              <Input
                type={"text"}
                placeholder="Existing Active"
                label="Existing Active"
                value={gridRow.activeName}
                readOnly={true}
              />
            </div>
          )}
          <Button
            label={"New Line"}
            Icon={Plus}
            disabled={
              gridRow.chargeId ||
              gridRow.chargeType ||
              gridRow.chargeValue ||
              editMode
            }
            onClick={addNewLine}
          />
          <Button
            label="Add"
            Icon={Plus}
            editMode={editMode}
            onClick={handleAdd}
            disabled={editMode ? true : false}
          />
          <Button
            label={"Reset"}
            Icon={RefreshCcw}
            onClick={resetForm}
            disabled={editMode ? true : false}
          />
          <div className="four fields">
            <Input
              type={"text"}
              placeholder="Tot Amount"
              label="Tot Amount"
              value={entireAmount}
              readOnly={true}
            />
            <Input
              type={"text"}
              placeholder="Tot Tax"
              label="Tot Tax"
              value={entireTax}
              readOnly={true}
            />
            <Input
              type={"text"}
              placeholder="Total"
              label="Total"
              value={total}
              readOnly={true}
            />
          </div>

          <Grid
            cellClickHandler={(e) => {
              console.log("cellClickHandler", e.data.taxList);
              setSelectedCharge({
                jobEvInvoicesTaxDetailList: e.data.taxList,
              });
            }}
            content={chargeList}
            isLoading={isDataAdding}
            // heading={`Total: ${total}`}
            searchable={false}
            stopEditingWhenCellsLoseFocus={true}
            onCellEditingStopped={cellEditorParams}
            config={[
              {
                headerName: "Job/Charge code",
                field: "chargeId",
                cellRendererSelector: (param) => {
                  console.log("select param", param);
                  if (!param.data.newLine) {
                    return {
                      component: ResizableScrollingTextRenderer,
                      params: {
                        isCustomValue: true,
                        customValue: param.data.chargeName,
                      },
                    };
                  } else {
                    return {
                      component: SelectRenderer,
                      params: {
                        onChangeHandler: (data) => {
                          setChargeList((prev) =>
                            prev.map((item) => {
                              if (!param.data.dcdId) {
                                if (item.id === param.data.id) {
                                  return {
                                    ...item,
                                    chargeId: parseInt(data),
                                  };
                                }
                              } else {
                                if (item.dcdId === param.data.dcdId) {
                                  return {
                                    ...item,
                                    chargeId: parseInt(data),
                                  };
                                }
                              }
                              return item;
                            })
                          );
                        },
                        options: jobCodesOptions,
                        module: MODULES.Finance,
                        field: "chargeId",
                        placeholder: "Select Job",
                        value: param.data.chargeId,
                        disabled: editMode ? true : false,
                        searchable: true,
                      },
                    };
                  }
                },
              },
              {
                headerName: "Charge Type",
                field: "chargeType",
                cellRendererSelector: (param) => {
                  if (!param.data.newLine) {
                    return {
                      component: ResizableScrollingTextRenderer,
                      params: {
                        isCustomValue: true,
                        customValue: param.data.chargeTypeName,
                      },
                    };
                  } else {
                    return {
                      component: SelectRenderer,
                      params: {
                        onChangeHandler: (data) => {
                          setChargeList((prev) =>
                            prev.map((item) => {
                              if (!param.data.dcdId) {
                                if (item.id === param.data.id) {
                                  return {
                                    ...item,
                                    chargeType: parseInt(data),
                                  };
                                }
                              } else {
                                if (item.dcdId === param.data.dcdId) {
                                  return {
                                    ...item,
                                    chargeType: parseInt(data),
                                  };
                                }
                              }
                              return item;
                            })
                          );
                        },
                        options: chargeOpt,
                        module: MODULES.Finance,
                        field: "chargeType",
                        placeholder: "Select Charge Type",
                        value: param.data.chargeType,
                        disabled: editMode ? true : false,
                        searchable: true,
                      },
                    };
                  }
                },
              },
              {
                headerName: "Existing Value",
                field: "existingTotalValue",
                cellRenderer: DynamicCurrencyRenderer,
                cellRendererParams: (params) => ({
                  currencyCode: userCurrencyCode,
                }),
              },
              {
                headerName: "Amount",
                field: "chargeValue",
                cellRendererSelector: (param) => {
                  if (!param.data.newLine) {
                    return {
                      component: CurrencyRenderer,
                      params: {
                        formatter: formatter,
                        value: parseFloat(param.data.chargeValue),
                      },
                    };
                  } else {
                    return {
                      component: CurrencyRenderer,
                      params: {
                        formatter: formatter,
                        cellEditorFramework: "agNumberCellEditor",
                        value: parseFloat(param.data.chargeValue),
                        tooltipValueGetter: (params) => {
                          return (
                            params.data.newLine &&
                            "press Enter to set the value"
                          );
                        },
                      },
                    };
                  }
                },
                editable: (params) => params.data.newLine && !editMode,
              },
              {
                headerName: "Qty",
                field: "qty",
                cellRendererSelector: (param) => {
                  if (!param.data.newLine) {
                    return {
                      component: () => param.data.qty,
                    };
                  } else {
                    return {
                      cellEditorFramework: "agNumberCellEditor",
                      value: parseFloat(param.data.qty),
                      tooltipValueGetter: (params) => {
                        return (
                          params.data.newLine && "press Enter to set the value"
                        );
                      },
                      editable: () => {
                        return editMode ? false : true;
                      },
                    };
                  }
                },
                editable: (params) => params.data.newLine && !editMode,
              },
              {
                headerName: "Tax Amount",
                field: "taxAmount",
                cellRenderer: DynamicCurrencyRenderer,
                cellRendererParams: (params) => ({
                  currencyCode: userCurrencyCode,
                }),
              },
              {
                headerName: "Total",
                field: "totalValue",
                cellRenderer: DynamicCurrencyRenderer,
                cellRendererParams: (params) => ({
                  currencyCode: userCurrencyCode,
                }),
              },
              {
                headerName: "UOM",
                field: "uom",
                cellRendererSelector: (param) => {
                  if (!param.data.newLine) {
                    return {
                      component: () => param.data.uomName,
                    };
                  } else {
                    return {
                      component: SelectRenderer,
                      params: {
                        onChangeHandler: (data) => {
                          setChargeList((prev) =>
                            prev.map((item) => {
                              if (!param.data.dcdId) {
                                if (item.id === param.data.id) {
                                  return {
                                    ...item,
                                    uom: parseInt(data),
                                  };
                                }
                              } else {
                                if (item.dcdId === param.data.dcdId) {
                                  return {
                                    ...item,
                                    uom: parseInt(data),
                                  };
                                }
                              }
                              return item;
                            })
                          );
                        },
                        options: uomOpt,
                        placeholder: "Select UOM",
                        value: param.data.uom,
                        disabled: editMode ? true : false,
                      },
                    };
                  }
                },
              },
              {
                headerName: "Credit/Debit",
                field: "noteTypeName",
              },
              {
                headerName: "Status",
                field: "status",
                cellRendererSelector: (param) => {
                  if (!param.data.newLine) {
                    return {
                      component: () => param.data.statusName,
                    };
                  } else {
                    return {
                      component: SelectRenderer,
                      params: {
                        onChangeHandler: (data) => {
                          setChargeList((prev) =>
                            prev.map((item) => {
                              if (!param.data.dcdId) {
                                if (item.id === param.data.id) {
                                  return {
                                    ...item,
                                    status: parseInt(data),
                                  };
                                }
                              } else {
                                if (item.dcdId === param.data.dcdId) {
                                  return {
                                    ...item,
                                    status: parseInt(data),
                                  };
                                }
                              }
                              return item;
                            })
                          );
                        },
                        options: statusOpt,
                        placeholder: "Select Status",
                        value: param.data.status,
                        disabled: editMode ? true : false,
                      },
                    };
                  }
                },
              },
              // {
              //   headerName: "Active",
              //   field: "active",
              //   cellRendererSelector: (param) => {
              //     if (!param.data.newLine) {
              //       return {
              //         component: () => param.data.activeName,
              //       };
              //     } else {
              //       return {
              //         component: SelectRenderer,
              //         params: {
              //           onChangeHandler: (data) => {
              //             setChargeList((prev) =>
              //               prev.map((item) => {
              //                 if (!param.data.dcdId) {
              //                   if (item.id === param.data.id) {
              //                     return {
              //                       ...item,
              //                       active: parseInt(data),
              //                     };
              //                   }
              //                 } else {
              //                   if (item.dcdId === param.data.dcdId) {
              //                     return {
              //                       ...item,
              //                       active: parseInt(data),
              //                     };
              //                   }
              //                 }
              //                 return item;
              //               })
              //             );
              //           },
              //           options: activeOpt,
              //           placeholder: "Select Active",
              //           value: param.data.active,
              //           disabled: editMode ? true : false,
              //         },
              //       };
              //     }
              //   },
              // },
              {
                headerName: "Edit",
                cellRenderer: ButtonRenderer,
                cellRendererParams: (params) => ({
                  clickHandler: () => {
                    if (params.data.newLine) return;
                    setGridRow(params.data);
                  },
                  label: "Edit",
                  disabled:
                    params.data.newLine || editMode || !cdNotePermission,
                }),
              },
              {
                headerName: "Delete",
                cellRenderer: ButtonRenderer,
                cellRendererParams: (params) => ({
                  clickHandler: () => {
                    console.log("Deleting Row:", params.data);
                    if (params.data.dcdId) {
                      setChargeList((prevTaxes) =>
                        prevTaxes.filter(
                          (charge) => charge.dcdId !== params.data.dcdId
                        )
                      );
                    } else {
                      setChargeList((prevTaxes) =>
                        prevTaxes.filter(
                          (charge) => charge.id !== params.data.id
                        )
                      );
                    }

                    setRowCount((prevCount) => prevCount - 1);
                  },
                  label: "Delete",
                  disabled: editMode || !cdNotePermission,
                  className: "ui red button",
                }),
              },
            ]}
          />
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              marginTop: "5px",
            }}
          >
            <div className="field" style={{ width: "100%" }}>
              <Button
                label={"Update Remark"}
                isLoading={isSubmitLoading}
                onClick={handleSave}
                Icon={FaRegBookmark}
                disabled={!cdActive || !cdNotePermission}
              />
              <Button
                label={"Print"}
                isLoading ={isDownloading}
                Icon={Printer}
                onClick={() =>
                  printOptionProceed(
                    creditDebitDetails ? creditDebitDetails : {}
                  )
                }
                disabled={!editMode || !cdActive || !printPermission}
              />
              <Button
                label={"Pre Print"}
                Icon={Printer}
                isLoading={isPrePrintedDownloading}
                onClick={() =>
                  prePrintOptionProceed(
                    creditDebitDetails ? creditDebitDetails : {}
                  )
                }
                disabled={!editMode || !cdActive || !printPermission}
              />
            </div>
          </div>
          {editMode && (
            <InfoList
              title="User Information"
              content={[
                {
                  title: "Created By",
                  value: creditDebitDetails?.createdBy
                    ? creditDebitDetails?.createdBy
                    : "N/A",
                },
                {
                  title: "Created Date",
                  value: creditDebitDetails?.createdDate
                    ? formatFromJsonDate(creditDebitDetails?.createdDate)
                    : "N/A",
                },
                {
                  title: "Last Modified By",
                  value: creditDebitDetails?.lastModifiedBy
                    ? creditDebitDetails?.lastModifiedBy
                    : "N/A",
                },
                {
                  title: "Last Modified Date",
                  value: creditDebitDetails?.lastModifiedDate
                    ? formatFromJsonDate(creditDebitDetails?.lastModifiedDate)
                    : "N/A",
                },
              ]}
              Icon={User}
            />
          )}
        </div>
      )}
    </>
  );
};

export default CreateCreditDebitNote;
